import { HttpHeaders } from '@angular/common/http'
import { format } from 'date-fns'
import { Call } from './calls.service'
import { User } from './users.service'

export function isEmailValid(email: string): boolean {
  const emailRegex =
    /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i
  return emailRegex.test(email)
}

export type DocumentUploadInfo = {
  key: string
  extension: string
  fileName: string
}

export function constructNameForUser(user: Pick<User, 'lastName' | 'firstName'>): string {
  return `${user.firstName} ${user.lastName}`
}

export function convertExtensionToMimeType(extension: 'csv' | 'pdf' | 'docx' | 'json') {
  if (extension === 'csv') {
    return 'text/csv'
  } else if (extension === 'pdf') {
    return 'application/pdf'
  } else if (extension === 'docx') {
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  } else if (extension === 'json') {
    return 'application/json'
  }
  throw new Error(`Unknown extension: ${extension}.`)
}

export function generateHeadersWithTimezone(): HttpHeaders {
  return new HttpHeaders({
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  })
}

export function formatDateWithMilliseconds(date: Date) {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX")
}

type ExportType = 'transcript' | 'minutes' | 'outline' | 'action-items'

export function generateFileName(call: Call, type: ExportType, extension: string) {
  const typeToBeautifiedType = {
    transcript: 'Transcript',
    minutes: 'Minutes',
    outline: 'Outline',
    'action-items': 'Action Items',
  }
  return `${typeToBeautifiedType[type]} for ${call.name}, ${format(call.date, 'dd-MM-yyyy')}.${extension}`
}

export function stringToColor(str: string): string {
  let hash = 0
  for (var i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash) + str.charCodeAt(str.length - 1)
  }
  let color = '#'
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff
    color += value.toString(16).padStart(2, '0')
  }
  return color
}
export function formatCounterpartyNames(counterpartyNames: string[]): string {
  return counterpartyNames.join(', ')
}

export function uniqueBy<T, K extends keyof T>(array: T[], key: K): T[] {
  return Object.values(
    array.reduce(
      (result, item) => {
        const value = item[key] as string
        if (!result[value]) {
          result[value] = item
        }
        return result
      },
      {} as { [key: string]: T }
    )
  )
}

export function stringArraysEqual(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index])
}

export function isEqual(a: any, b: any): boolean {
  const ok = Object.keys,
    tx = typeof a,
    ty = typeof b
  return a && b && tx === 'object' && tx === ty ? ok(a).length === ok(b).length && ok(a).every((key) => isEqual(a[key], b[key])) : a === b
}

export function removeNullValues(object: any) {
  return Object.fromEntries(Object.entries(object).filter(([_, value]) => value !== null))
}

export function getColorPalette(length: number): string[] {
  const brandColors = ['#E5771F', '#5A3E2B', '#76C7AE', '#D4E54F', '#FFF1C2']

  if (length <= brandColors.length) {
    return brandColors.slice(0, length)
  }

  const extraColorsNeeded = length - brandColors.length
  const generatedColors = Array.from({ length: extraColorsNeeded }, (_, i) => {
    const hue = (i * 360) / extraColorsNeeded
    return `hsl(${hue}, 65%, 65%)`
  })

  return [...brandColors, ...generatedColors]
}

export function getFileName(file: File): string {
  const name = file.name
  return name.substring(0, name.lastIndexOf('.'))
}

export function getFileExtension(file: File): string {
  return file.name.split('.').pop()!.toLowerCase()
}

export function truncateStringFromMiddle(text: string, maxLength: number = 20): string {
  if (text.length <= maxLength) {
    return text
  }
  const ellipsis = '...'
  const charsToShow = maxLength - ellipsis.length
  const frontChars = Math.ceil(charsToShow / 2)
  const backChars = Math.floor(charsToShow / 2)
  return text.substring(0, frontChars) + ellipsis + text.substring(text.length - backChars)
}

export function generatePlaceholderId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
}

export function formatTimeUntil(date: Date | string | null): string | null {
  if (date === null) {
    return null
  }

  const targetDate = typeof date === 'string' ? new Date(date) : date
  if (isNaN(targetDate.getTime())) {
    return null
  }

  const now = new Date()
  const diffMs = targetDate.getTime() - now.getTime()

  if (diffMs <= 0) {
    return null
  }

  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays > 0) {
    return `${diffDays}d`
  } else if (diffHours > 0) {
    const remainingMinutes = diffMinutes % 60
    if (remainingMinutes > 0) {
      return `${diffHours}h ${remainingMinutes}m`
    }
    return `${diffHours}h`
  } else {
    return `${diffMinutes}m`
  }
}

export function isDateTooFarInPastForManualCallCreation(date: Date | null): boolean {
  if (date === null) {
    return false
  }
  const now = new Date()
  const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60 * 1000)
  return date < fifteenMinutesAgo
}

export function convertTextToProseMirrorJson(text: string): string {
  const placeholderRegex = /\{\{([^}]+)\}\}/g
  const matches = Array.from(text.matchAll(placeholderRegex))

  if (matches.length === 0) {
    return JSON.stringify({
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          attrs: { align: null, indent: null },
          content: [{ type: 'text', text }],
        },
      ],
    })
  }

  const contentParts = matches.flatMap((match, matchIndex) => {
    const previousMatchEnd = matchIndex === 0 ? 0 : matches[matchIndex - 1].index! + matches[matchIndex - 1][0].length
    const textBeforeMatch = text.substring(previousMatchEnd, match.index!)

    const textPart = textBeforeMatch.length > 0 ? [{ type: 'text' as const, text: textBeforeMatch }] : []
    const placeholderPart = {
      type: 'placeholder' as const,
      attrs: { id: generatePlaceholderId() },
    }

    return [...textPart, placeholderPart]
  })

  const lastMatch = matches[matches.length - 1]
  const textAfterLastMatch = text.substring(lastMatch.index! + lastMatch[0].length)
  const finalTextPart = textAfterLastMatch.length > 0 ? [{ type: 'text' as const, text: textAfterLastMatch }] : []

  return JSON.stringify({
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        attrs: { align: null, indent: null },
        content: [...contentParts, ...finalTextPart],
      },
    ],
  })
}
